import React, { useState } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../services/firebaseConfig';

const APP_ID = 'worldgrad-crm';

export default function AuthScreen({ logoUrl }) {
  const [isRegistering, setIsRegistering] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // New state for visibility toggle
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email.trim());
      setMessage("A password reset link has been sent to your email address.");
      setLoading(false);
    } catch (err) {
      setError("Failed to send reset email. Please ensure the email address is correct.");
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegistering) {
        if (!name.trim()) {
          setError("Full name is required to request access.");
          setLoading(false);
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
        
        await updateProfile(userCredential.user, {
          displayName: name.trim()
        });

        const userDocRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'users', userCredential.user.uid);
        await setDoc(userDocRef, {
          uid: userCredential.user.uid,
          email: email.trim(),
          name: name.trim(),
          role: 'User',
          status: 'Pending',
          createdAt: Timestamp.now(),
          lastLogin: Timestamp.now()
        });

      } else {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      }
    } catch (err) {
      let msg = err.message.replace('Firebase:', '').trim();
      if (msg.includes('invalid-credential') || msg.includes('user-not-found')) {
        msg = isRegistering 
          ? "This email is already in use. Please sign in instead."
          : "Invalid credentials. If you are a new user, please click 'Request Access' below.";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-full flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl shadow-[#0464AD]/10 border border-slate-100 p-10 animate-in fade-in zoom-in-95 duration-500">
        <div className="flex flex-col items-center mb-10">
          {logoUrl && <img src={logoUrl} alt="Worldgrad Logo" className="h-12 w-auto mb-6" />}
          <h2 className="text-2xl font-black text-slate-900 tracking-tighter">
            {isForgotPassword ? 'Reset Password' : (isRegistering ? 'Request System Access' : 'TWG Application Portal')}
          </h2>
          <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-2">
            Submit and track appications
          </p>
        </div>

        <form onSubmit={isForgotPassword ? handleForgotPassword : handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
            <input 
              type="email" required className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" 
              value={email} onChange={e => setEmail(e.target.value)} placeholder="agent@worldgrad.com" 
            />
          </div>

          {!isForgotPassword && (
            <>
              {isRegistering && (
                <div className="space-y-1.5 animate-in slide-in-from-top-2">
                  <label className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest ml-1">Full Name</label>
                  <input 
                    type="text" required className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" 
                    value={name} onChange={e => setName(e.target.value)} placeholder="Enter your name" 
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center pr-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Password</label>
                  {!isRegistering && (
                    <button 
                      type="button" 
                      onClick={() => { setIsForgotPassword(true); setError(''); setMessage(''); }}
                      className="text-[9px] font-black text-[#0464AD] uppercase hover:underline"
                    >
                      Forgot?
                    </button>
                  )}
                </div>
                <div className="relative group">
                  <input 
                    type={showPassword ? "text" : "password"} // Dynamic type based on state
                    required className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 pr-12 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" 
                    value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" 
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0464AD] transition-colors"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a10.05 10.05 0 013.273-4.508M9.88 9.88l4.24 4.24M10.73 5.08A10.43 10.43 0 0112 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-1.274 0-2.484-.23-3.605-.65M3 3l18 18" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {error && <div className="text-xs text-[#F15E3B] font-bold text-center mt-2 p-3 bg-red-50 rounded-xl border border-red-100">{error}</div>}
          {message && <div className="text-xs text-emerald-500 font-bold text-center mt-2 p-3 bg-emerald-50 rounded-xl border border-emerald-100">{message}</div>}

          <button 
            type="submit" disabled={loading} 
            className="w-full bg-[#0464AD] text-white py-4 rounded-2xl font-black shadow-xl shadow-[#0464AD]/20 hover:bg-[#0091D0] transition-all active:scale-95 disabled:opacity-50 mt-4 uppercase tracking-widest"
          >
            {loading ? 'PROCESSING...' : (isForgotPassword ? 'SEND RESET LINK' : (isRegistering ? 'SUBMIT REQUEST' : 'SIGN IN'))}
          </button>
        </form>

        <div className="mt-8 pt-8 border-t border-slate-100 text-center">
          <button 
            onClick={() => { 
              setIsRegistering(!isRegistering); 
              setIsForgotPassword(false);
              setShowPassword(false);
              setError(''); 
              setMessage('');
            }} 
            className="text-xs font-bold text-[#0464AD] hover:underline uppercase tracking-tight"
          >
            {isRegistering || isForgotPassword ? 'Back to Sign In' : 'New User? Request Access'}
          </button>
        </div>
      </div>
    </div>
  );
}